import React, {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react'
import {Stack} from '@mui/material'
import {useLocation, useNavigate} from 'react-router-dom'
import UserNavMenu from '@/components/UserNavMenu'
import logoImage from '@/assets/images/logo.png'
import HoverButton from '@/components/UI/button/HoverButton'
import AcceptModal from '@/components/AcceptModal'
import {SET_IS_NEED_AUTH} from '@/store/slices'
import {SET_GUEST_MODE} from '@/store/slices/storage'
import ClassicAcceptModal from '@/components/AcceptModal/Classic'
import {useAppDispatch, useAppSelector} from '@/hooks/redux'
import {selectGuestMode} from '@/store/selectors/storage'
import useAcceptModal from '@/hooks/modalAccept'

import {selectUser} from '@/store/selectors'
import {ReactComponent as MainIcon} from '@/assets/svgs/MapIcon/buttons/MainIcon.svg'
import {ReactComponent as ExtrasIcon} from '@/assets/svgs/MapIcon/buttons/ExtrasIcon.svg'
import {ReactComponent as AnalyticsIcon} from '@/assets/svgs/MapIcon/buttons/MapModesIcon.svg'
import {ReactComponent as HexaTradingIcon} from '@/assets/svgs/MapIcon/buttons/HexaTradingIcon.svg'
import {ReactComponent as InventoryIcon} from '@/assets/svgs/MapIcon/buttons/InventoryIcon.svg'
import {ReactComponent as StoreIcon} from '@/assets/svgs/MapIcon/buttons/StoreIcon.svg'
import {colors} from '@/constants'
import {useTranslation} from 'react-i18next'
import useIsMarketMaker from '@/hooks/useIsMarketMaker'

const IS_ANALYTICS_ENABLED = false

const buttonsData = [
  {name: '1030', icon: MainIcon, url: '/main'},
  {name: '1833', icon: ExtrasIcon, url: '/extras'},
  ...(IS_ANALYTICS_ENABLED
    ? [
        {
          name: '1031',
          icon: AnalyticsIcon,
          url: '/analytics',
        },
      ]
    : []),
  {name: '1032', url: '/trading', icon: HexaTradingIcon},
  {
    name: '1033',
    url: '/inventory',
    icon: InventoryIcon,
  },
  {
    name: '1034',
    icon: StoreIcon,
    url: '/store',
  },
]

const marketMakerButton = {
  name: '4MM',
  icon: StoreIcon,
  url: '/mm',
}

const Index = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const {setAcceptModal} = useAcceptModal()
  const guestMode = useAppSelector(selectGuestMode)
  const user = useAppSelector(selectUser)
  const buttonsContainerRef = useRef<HTMLDivElement>(null)
  const [showIcons, setShowIcons] = useState(true)
  const {isMarketMaker} = useIsMarketMaker(Boolean(user))

  const buttons = useMemo(
    () => (isMarketMaker ? [...buttonsData, marketMakerButton] : buttonsData),
    [isMarketMaker]
  )

  useEffect(() => {
    const checkContainerSize = () => {
      if (!buttonsContainerRef.current) return

      const containerWidth = buttonsContainerRef.current.offsetWidth
      const threshold = 800
      setShowIcons(containerWidth >= threshold)
    }

    checkContainerSize()

    const resizeObserver = new ResizeObserver(checkContainerSize)
    if (buttonsContainerRef.current) {
      resizeObserver.observe(buttonsContainerRef.current)
    }

    window.addEventListener('resize', checkContainerSize)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', checkContainerSize)
    }
  }, [])

  const handleNotUser = () => {
    setAcceptModal(
      <AcceptModal
        zIndex={1400}
        handleAgree={() => {
          dispatch(SET_IS_NEED_AUTH('AUTH'))
          navigate('/')
        }}
        handleCancel={() => dispatch(SET_GUEST_MODE(true))}
        confirmText={'1005'}
        cancelText="1143">
        <ClassicAcceptModal name="1142" description="1144" />
      </AcceptModal>
    )
  }

  useLayoutEffect(() => {
    if (!user && !guestMode) {
      handleNotUser()
    }
  }, [guestMode])

  const handleClickTab = (index: number) => {
    if (user) {
      navigate(buttons[index].url)
    } else {
      handleNotUser()
    }
  }

  return (
    <Stack
      marginTop="-10px"
      paddingBottom="5px"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      position="relative">
      <Stack
        position="relative"
        maxWidth="303px"
        height="100%"
        sx={{
          transition: 'filter 0.1s ease',
          '&:hover': {
            filter: 'brightness(1.5)',
          },
        }}
        onClick={() => navigate('/')}>
        <img src={logoImage} style={{width: '100%', cursor: 'pointer'}} />
      </Stack>
      {location.pathname !== '/marketplace' &&
        location.pathname !== '/store-loi' && (
          <Stack
            ref={buttonsContainerRef}
            direction="row"
            gap="10px"
            width="100%"
            boxSizing="border-box"
            justifyContent="space-between">
            {buttons.map((buttonData, index) => {
              const IconComponent = buttonData.icon

              return (
                <HoverButton
                  key={'mainButton' + index}
                  onClick={() => handleClickTab(index)}
                  fontSize="18px"
                  fontWeight="500"
                  height="45px"
                  textColor={
                    location.pathname === buttonData.url
                      ? '#FF9326'
                      : colors.white
                  }
                  boxShadow="0px 0px 10px 0px #FFF17233"
                  boxShadowHover="0px 0px 10px 0px #FFF17266 inset"
                  width="inherit"
                  padding="10px 20px"
                  gap="10px"
                  maxHeight="50px"
                  sx={{position: 'relative', overflow: 'hidden'}}>
                  <span style={{display: 'inline-block'}}>
                    {t(buttonData.name)}
                  </span>
                  {showIcons && (
                    <IconComponent
                      style={{
                        position: 'absolute',
                        opacity: 1,
                        right: '10px',
                        width: '11%',
                        height: 'auto',
                        fill:
                          location.pathname === buttonData.url
                            ? '#FF9326'
                            : colors.white,
                        maxWidth: '100%',
                        maxHeight: '100%',
                      }}
                    />
                  )}
                </HoverButton>
              )
            })}
          </Stack>
        )}
      <UserNavMenu user={user} />
    </Stack>
  )
}

export default Index
